/**
 * Line-item pricing grid for the supplier quote form.
 *
 * Pure presentational: receives the normalised line responses (numbers, not
 * API strings) and reports edits back. Disabled once the RFQ is closed.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toNumber } from './rfqFormat';

export interface LineItemResponse {
  rfq_line_item: number;
  line_number: number;
  item_description: string;
  quantity: number;
  unit_of_measure: string;
  target_unit_price: number | null;
  unit_price: number;
  lead_time_days: number | null;
  /** Unit the price is per; '' means the RFQ line's own UOM. */
  quoted_uom: string;
  notes: string;
}

interface SupplierResponseLineItemsProps {
  lines: LineItemResponse[];
  currency: string;
  disabled: boolean;
  formatMoney: (value: unknown) => string;
  onUpdate: (index: number, field: keyof LineItemResponse, value: number | string | null) => void;
}

export default function SupplierResponseLineItems({
  lines,
  currency,
  disabled,
  formatMoney,
  onUpdate,
}: SupplierResponseLineItemsProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Line Item Pricing</CardTitle>
        {/* Once the RFQ is awarded, cancelled or past its deadline this panel is
            a record of what was quoted, not a form. Telling the reader to enter
            prices into fields they cannot type in is an instruction to do the
            impossible. */}
        <CardDescription>
          {disabled ? 'The prices you quoted for each item' : 'Enter your unit prices for each item'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {lines.map((item, index) => (
            <div key={item.rfq_line_item} className="grid grid-cols-7 gap-3 p-3 border rounded-lg">
              <div className="col-span-2">
                <Label className="text-xs text-muted-foreground">Item {item.line_number}</Label>
                <p className="font-medium text-sm">{item.item_description}</p>
                <p className="text-xs text-muted-foreground">
                  Qty: {toNumber(item.quantity).toLocaleString()} {item.unit_of_measure}
                </p>
                {item.target_unit_price !== null && (
                  <p className="text-xs text-muted-foreground">
                    Target: {formatMoney(item.target_unit_price)}
                  </p>
                )}
              </div>
              <div>
                {/* The asterisk marks a field the reader must fill in. On a
                    closed response there is nothing left to fill in. */}
                <Label className="text-xs">Unit Price ({currency}){disabled ? '' : ' *'}</Label>
                <Input
                  type="number"
                  value={item.unit_price || ''}
                  onChange={(e) => onUpdate(index, 'unit_price', parseFloat(e.target.value) || 0)}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  disabled={disabled}
                />
              </div>
              <div>
                {/* A supplier pricing per box, kg or metre rather than per the
                    RFQ's unit says so here; the buyer's comparison flags the
                    line so a per-box price is never read as per-each. */}
                <Label className="text-xs">Priced per</Label>
                <Input
                  value={item.quoted_uom}
                  onChange={(e) => onUpdate(index, 'quoted_uom', e.target.value)}
                  placeholder={item.unit_of_measure || 'unit'}
                  maxLength={20}
                  disabled={disabled}
                  title={`Leave blank to price per ${item.unit_of_measure || 'unit'}`}
                />
                {item.quoted_uom && item.quoted_uom.trim().toLowerCase() !== (item.unit_of_measure || '').toLowerCase() && (
                  <p className="text-[11px] text-amber-700 mt-1">Differs from the requested {item.unit_of_measure || 'unit'}</p>
                )}
              </div>
              <div>
                <Label className="text-xs">Lead Time (days)</Label>
                <Input
                  type="number"
                  value={item.lead_time_days ?? ''}
                  onChange={(e) => onUpdate(index, 'lead_time_days', e.target.value ? parseInt(e.target.value, 10) : null)}
                  min="0"
                  placeholder="14"
                  disabled={disabled}
                />
              </div>
              <div>
                <Label className="text-xs">Line Total</Label>
                <div className="h-9 flex items-center font-medium">
                  {formatMoney(toNumber(item.unit_price) * toNumber(item.quantity))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Input
                  value={item.notes}
                  onChange={(e) => onUpdate(index, 'notes', e.target.value)}
                  placeholder="Optional notes"
                  disabled={disabled}
                />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
